import React, { useEffect } from 'react';

import Checkbox from 'components/partials/checkbox/checkbox';
import Loader from 'components/partials/loader/loader';

interface CustomLogicCategory {
  category: string;
}

interface CustomLogicDropdownProps {
  categories: CustomLogicCategory[];
  activeCategoryIds: string[];
  onToggleCategory: (category: string) => void;
  onClose: () => void;
  isLoading: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

const CustomLogicDropdown = ({
  categories,
  activeCategoryIds,
  onToggleCategory,
  onClose,
  isLoading,
  containerRef,
}: CustomLogicDropdownProps): React.JSX.Element => {
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        containerRef?.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, containerRef]);

  return (
    <div
      className="absolute top-full right-0 mt-1 z-10 bg-white border border-navy-200 rounded-md pl-2 pr-0.5 py-2 min-w-[240px] max-h-64 overflow-y-auto scrollbar-thin"
      data-testid="custom-logic-dropdown"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader loading fullScreen={false} />
        </div>
      ) : categories.length === 0 ? (
        <div className="p-2 text-sm text-gray-500">No categories available</div>
      ) : (
        <div className="flex flex-col gap-1">
          {categories.map((category) => {
            const isActive = activeCategoryIds.includes(category.category);

            return (
              <div
                key={category.category}
                className="p-2 rounded-md hover:bg-gray-50"
                data-testid={`custom-logic-category-${category.category}`}
              >
                <Checkbox
                  labelFor={`custom-logic-${category.category}`}
                  label={category.category}
                  checkboxValue={category.category}
                  checked={isActive}
                  onChange={() => onToggleCategory(category.category)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomLogicDropdown;
